import { useEffect } from "react";

import { tactile, type TactileIntent } from "./tactile";

/**
 * Тактильная поверхность приложения (FE-14).
 *
 * Правило слоя: **визуальный `press` — это и тактильный `press`**. Слой
 * движения уже связал нажатие с утилитами `press` и `press-row`; отклик
 * садится на ту же связь, поэтому новый экран отзывается сам, без строчки в
 * компоненте. Раздавать отклик вручную в каждом `onClick` пробовали — так он
 * и разъехался: половина интерактива молчала, потому что про неё забыли.
 *
 * Слушатель ровно один, на документе. Событие — `pointerdown`, а не `click`:
 * нативно палец чувствует кнопку в момент касания, и это же момент, когда
 * срабатывает `:active` из FE-13. На iOS `:active` вообще держится на no-op
 * слушателе `touchstart` из `src/main.tsx` — там же объяснено почему.
 */

/** Намерение по умолчанию для элементов, у которых нет `data-haptic`. */
const NAVIGATES = ".press-row, a[href]";
const PRESSABLE = [
  "button",
  '[role="button"]',
  '[role="switch"]',
  '[role="tab"]',
  // Нативные переключатели: сам `input` скрыт `sr-only`, но клик по подписи
  // приходит и на него тоже. `label` в список не входит намеренно — иначе
  // гудело бы каждое касание текстового поля, обёрнутого подписью.
  'input[type="checkbox"]',
  'input[type="radio"]',
  ".press",
].join(", ");

/** Значения `data-haptic`, которые делегат принимает как намерение. */
const INTENTS = new Set<string>([
  "tap",
  "nav",
  "press",
  "commit",
  "select",
  "surface",
  "gain",
  "drawdown",
  "success",
  "error",
  "warning",
]);

function isInert(element: Element): boolean {
  return element.matches(":disabled, [disabled], [aria-disabled='true']");
}

/**
 * Намерение для цели события либо `undefined`, если под пальцем не интерактив.
 *
 * Порядок важен: явный `data-haptic` перебивает всё (в том числе выключает
 * отклик значением `off`), иначе переход отличается от нажатия, а нажатие —
 * от пустого места.
 */
export function resolveIntent(target: EventTarget | null): TactileIntent | undefined {
  if (!(target instanceof Element)) return undefined;

  const declared = target.closest<HTMLElement>("[data-haptic]");
  if (declared) {
    const value = declared.dataset.haptic ?? "";
    if (value === "off") return undefined;
    if (INTENTS.has(value)) return isInert(declared) ? undefined : (value as TactileIntent);
    // Неизвестное значение — опечатка в атрибуте. Молча гасить отклик хуже,
    // чем отработать по общим правилам ниже.
  }

  const navigates = target.closest<HTMLElement>(NAVIGATES);
  const pressable = target.closest<HTMLElement>(PRESSABLE);

  // Ссылка, стилизованная под кнопку, — это всё-таки кнопка: побеждает тот,
  // кто ближе к пальцу.
  const element =
    navigates && pressable
      ? navigates.contains(pressable)
        ? pressable
        : navigates
      : (navigates ?? pressable);

  if (!element || isInert(element)) return undefined;
  return element === navigates ? "nav" : "tap";
}

export function useTactileSurface(): void {
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      // Не первичный указатель — второй палец мультитача; не нулевая кнопка —
      // правый клик и стилус с зажатой клавишей. Ни то, ни другое не нажатие.
      if (!event.isPrimary || event.button !== 0) return;

      const intent = resolveIntent(event.target);
      if (intent) tactile(intent);
    };

    document.addEventListener("pointerdown", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);
}
